import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAppBuild } from '~/types/build'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useUpdateBuildMutation } from '~/queries/builds'
import { displayToast } from '~/utils/toast'

interface FormProps {
  build: IAppBuild
  onClose: VoidFunction
  onSuccess: (build: IAppBuild) => void
}

const validationSchema = Yup.object().shape({
  description: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const ModalForm = ({ build, onClose, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues: build,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: updateBuildMutation } = useUpdateBuildMutation()

  /** Handlers */

  const handleFormSubmit = async (values: { description: string }) => {
    try {
      const newBuild = await updateBuildMutation({ ...build, ...values })
      onSuccess(newBuild)
    } catch (e) {
      captureException(e)
      displayToast('Build could not be updated', 'error')
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <TextInput
          {...register('description')}
          autoFocus
          error={errors.description?.message}
          label="Description"
          placeholder="Enter a description"
          required
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ModalUpdate = ({ isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Edit build</ModalHeader>
      <ModalForm {...props} onClose={onClose} />
    </Modal>
  )
}

export default ModalUpdate
